<template>
  <div>
    <!-- Toast / Banner Notification -->
    <transition name="slide-fade">
      <div v-if="toast.show" class="export-toast-banner" :class="toast.type">
        <div class="toast-icon">
          <i v-if="toast.type === 'error'" class="fas fa-exclamation-circle"></i>
          <i v-else class="fas fa-check-circle"></i>
        </div>
        <div class="toast-content">
          <div class="toast-title">{{ toast.title || 'Export Notification' }}</div>
          <div class="toast-message">{{ toast.message }}</div>
        </div>
        <button class="toast-close" @click="toast.show = false">&times;</button>
      </div>
    </transition>

    <!-- Main Modal Mask Overlay -->
    <transition name="modal-fade">
      <div v-if="visible && !showCompleteModal" class="export-modal-backdrop" @click.self="handleCancel" @touchmove.self.prevent>
        <div class="export-modal-card">
          
          <!-- Header with Close Icon -->
          <div class="export-modal-header">
            <h2 class="modal-title">Download</h2>
            <button type="button" class="btn-modal-close" @click="handleCancel" aria-label="Close">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <p class="modal-subtitle">Select the data range you want to download</p>

          <!-- Date Section (Clicking opens custom Date Picker Dialog) -->
          <div class="form-group-custom">
            <label class="field-label">Date</label>
            <div class="date-single-box" @click="openDatePickerModal">
              <span class="date-display-text" :class="{ 'placeholder-mode': !formattedDateDisplay }">
                {{ formattedDateDisplay || 'Select Date' }}
              </span>
              <i class="fas fa-calendar-alt date-icon-right"></i>
            </div>
          </div>

          <!-- Hour / Shift Section -->
          <div class="form-group-custom">
            <label class="field-label">Hour</label>
            
            <!-- Shift Selector Chips -->
            <div class="shift-pills-row">
              <button
                type="button"
                class="shift-pill"
                :class="{
                  active: !businessDate && selectedShift === 'morning',
                  disabled: businessDate
                }"
                :disabled="businessDate"
                @click="selectShift('morning')"
              >
                <svg v-if="!businessDate && selectedShift === 'morning'" class="shift-check-icon" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12.5L9.5 18L20 6" stroke="#ffffff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Morning Shift</span>
              </button>

              <button
                type="button"
                class="shift-pill"
                :class="{
                  active: !businessDate && selectedShift === 'night',
                  disabled: businessDate
                }"
                :disabled="businessDate"
                @click="selectShift('night')"
              >
                <svg v-if="!businessDate && selectedShift === 'night'" class="shift-check-icon" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12.5L9.5 18L20 6" stroke="#ffffff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Night Shift</span>
              </button>

              <button
                type="button"
                class="shift-pill"
                :class="{
                  active: !businessDate && selectedShift === 'custom',
                  disabled: businessDate
                }"
                :disabled="businessDate"
                @click="selectShift('custom')"
              >
                <svg v-if="!businessDate && selectedShift === 'custom'" class="shift-check-icon" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12.5L9.5 18L20 6" stroke="#ffffff" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Custom</span>
              </button>
            </div>

            <!-- Start & End Time Fields (Without icons, soft grey-blue bg) -->
            <div class="time-inputs-row">
              <div class="time-input-col">
                <label class="sub-label">Start time</label>
                <div
                  class="time-field-box"
                  :class="{
                    'is-custom': selectedShift === 'custom' && !businessDate,
                    'disabled': businessDate || selectedShift !== 'custom'
                  }"
                  @click="onTimeFieldClick('start')"
                >
                  <span class="time-display-val">{{ startTime }}</span>
                </div>
              </div>

              <div class="time-input-col">
                <label class="sub-label">End time</label>
                <div
                  class="time-field-box"
                  :class="{
                    'is-custom': selectedShift === 'custom' && !businessDate,
                    'disabled': businessDate || selectedShift !== 'custom'
                  }"
                  @click="onTimeFieldClick('end')"
                >
                  <span class="time-display-val">{{ endTime }}</span>
                </div>
              </div>
            </div>

            <p class="shift-hint">
              {{ businessDate 
                ? 'Business date aktif: jam terisi otomatis mencakup satu cycle produksi penuh (07:00 – 06:59).' 
                : 'The shift fills the hours automatically. Pick Custom to set your own hours.' 
              }}
            </p>
          </div>

          <!-- PDC Section -->
          <div class="form-group-custom">
            <label class="field-label">
              PDC <span class="label-optional">(can select more than one)</span>
            </label>

            <!-- PDC Dropdown Wrapper (isolates menu positioning to the trigger field) -->
            <div class="pdc-dropdown-wrapper" ref="pdcDropdownWrapper">
              <!-- PDC Multi-Select Dropdown Header -->
              <div class="pdc-dropdown-trigger" @click="togglePdcDropdown">
                <span class="pdc-trigger-text" :class="{ 'is-placeholder': selectedPdcs.length === 0 }">
                  {{ pdcSummaryText }}
                </span>
                <i class="fas fa-caret-down caret-icon" :class="{ rotate: pdcDropdownOpen }"></i>
              </div>

              <!-- PDC Searchable List Popup (Dynamic Dropdown / Dropup) -->
              <div
                v-if="pdcDropdownOpen"
                class="pdc-dropdown-menu"
                :class="{ 'dropup-mode': pdcDropup }"
                :style="pdcMenuStyle"
              >
                <div class="pdc-search-header">
                  <input
                    type="text"
                    class="pdc-search-input"
                    placeholder="Search PDC..."
                    v-model="pdcSearchQuery"
                  />
                </div>

                <div class="pdc-options-list">
                  <div
                    v-for="item in filteredPdcs"
                    :key="item.pdc"
                    class="pdc-option-item"
                    @click="togglePdc(item.pdc)"
                  >
                    <div class="pdc-checkbox-box" :class="{ checked: selectedPdcs.includes(item.pdc) }">
                      <i v-if="selectedPdcs.includes(item.pdc)" class="fas fa-check pdc-check-icon"></i>
                    </div>
                    <span class="pdc-option-label">{{ item.name }}</span>
                  </div>
                </div>

                <!-- Footer with count and Apply button -->
                <div class="pdc-dropdown-footer">
                  <span class="pdc-selected-count">{{ selectedPdcs.length }} selected</span>
                  <button type="button" class="btn-pdc-apply" @click="applyPdcSelection">
                    Apply
                  </button>
                </div>
              </div>
            </div>

            <!-- Selected PDC Chips -->
            <div v-if="selectedPdcs.length > 0" class="pdc-chips-container">
              <div
                v-for="pdc in selectedPdcs"
                :key="'chip-' + pdc"
                class="pdc-chip"
              >
                <span>{{ getPdcName(pdc) }}</span>
                <button type="button" class="chip-remove" @click.stop="removePdc(pdc)" aria-label="Remove">&times;</button>
              </div>
            </div>
          </div>

          <!-- Excel Format Helper Text -->
          <p class="format-note">
            The file will be downloaded in Excel format (.xlsx)
          </p>

          <!-- Footer Actions -->
          <div class="modal-footer-actions">
            <button
              type="button"
              class="btn-modal-cancel"
              :disabled="isExporting"
              @click="handleCancel"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn-modal-download"
              :disabled="isDownloadDisabled"
              @click="handleDownload"
            >
              <i class="fas fa-download mr-1"></i>
              <span>Download</span>
            </button>
          </div>

        </div>
      </div>
    </transition>

    <!-- Custom Date Picker Dialog Modal (Exact Figma Design) -->
    <transition name="modal-fade">
      <div v-if="showDatePickerModal" class="datepicker-modal-backdrop" @click.self="closeDatePickerModal" @touchmove.self.prevent>
        <div class="datepicker-modal-card">
          <!-- Top Label -->
          <div class="picker-top-label">Select Date</div>
          
          <!-- Big Formatted Date Header -->
          <div class="picker-big-date">{{ pickerFormattedTitle }}</div>
          
          <hr class="picker-divider" />

          <!-- Month Navigation Row -->
          <div class="picker-month-row">
            <span class="picker-month-name">{{ pickerMonthYearText }}</span>
            <div class="picker-nav-arrows">
              <button type="button" class="btn-nav-arrow" @click="prevMonth">
                <i class="fas fa-chevron-left"></i>
              </button>
              <button type="button" class="btn-nav-arrow" @click="nextMonth">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          <!-- Business Date Toggle Row (Inside Datepicker matching Figma design) -->
          <div class="business-date-row" @click="togglePickerBusinessDate">
            <div class="custom-checkbox-box" :class="{ checked: pickerBusinessDate }">
              <i v-if="pickerBusinessDate" class="fas fa-check checkbox-check-icon"></i>
            </div>
            <div class="business-date-text-col">
              <div class="business-date-title">Business date</div>
              <div class="business-date-desc">
                {{ pickerBusinessDate ? 'On — 1 day selected, end date auto H+1' : 'Off — choose any start and end date' }}
              </div>
            </div>
          </div>

          <!-- Days of Week Header -->
          <div class="calendar-days-header">
            <span>S</span>
            <span>M</span>
            <span>T</span>
            <span>W</span>
            <span>T</span>
            <span>F</span>
            <span>S</span>
          </div>

          <!-- Calendar Days Grid -->
          <div class="calendar-days-grid">
            <div
              v-for="b in blankDaysBeforeMonth"
              :key="'blank-' + b"
              class="day-cell blank"
            ></div>

            <div
              v-for="d in daysInCurrentMonth"
              :key="'day-' + d"
              class="day-cell"
              :class="getDayCellClass(d)"
              @click="onDayClick(d)"
            >
              <span class="day-number">{{ d }}</span>
            </div>
          </div>

          <!-- Helper Hint Below Calendar -->
          <p class="picker-hint-text">
            {{ pickerBusinessDate 
              ? 'Business date on: click a start date, end date automatically H+1.'
              : 'Business date off: click a start date, then an end date, to select any range you need.' 
            }}
          </p>

          <!-- Footer Actions (Cancel / OK text buttons) -->
          <div class="datepicker-actions">
            <button type="button" class="btn-picker-action" @click="closeDatePickerModal">Cancel</button>
            <button type="button" class="btn-picker-action" :disabled="!pickerStartDate" @click="applyDatePicker">OK</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Custom Time Picker Dialog Modal (Exact Figma Design) -->
    <transition name="modal-fade">
      <div v-if="showTimePickerModal" class="timepicker-modal-backdrop" @click.self="closeTimePickerModal" @touchmove.self.prevent>
        <div class="timepicker-modal-card">
          <!-- Top Label -->
          <div class="timepicker-top-label">
            {{ timePickerTarget === 'start' ? 'Select Start Time' : 'Select End Time' }}
          </div>

          <!-- Time Input Digit Boxes -->
          <div class="timepicker-body">
            <!-- Hour Box Column -->
            <div class="timepicker-digit-col">
              <div
                class="timepicker-digit-box"
                :class="{ active: pickerActiveUnit === 'hour' }"
                @click="focusHourInput"
                @wheel="handleWheel($event, 'hour')"
              >
                <input
                  ref="timeHourInput"
                  type="text"
                  class="timepicker-num-input"
                  v-model="pickerHour"
                  maxlength="2"
                  inputmode="numeric"
                  @focus="pickerActiveUnit = 'hour'"
                  @blur="formatPickerHourOnBlur"
                  @input="handleHourInput"
                  @keydown="handleHourKeydown"
                />
              </div>
              <div class="timepicker-unit-label">Hour</div>
            </div>

            <!-- Colon separator -->
            <div class="timepicker-colon">:</div>

            <!-- Minute Box Column -->
            <div class="timepicker-digit-col">
              <div
                class="timepicker-digit-box"
                :class="{ active: pickerActiveUnit === 'minute' }"
                @click="focusMinuteInput"
                @wheel="handleWheel($event, 'minute')"
              >
                <input
                  ref="timeMinuteInput"
                  type="text"
                  class="timepicker-num-input"
                  v-model="pickerMinute"
                  maxlength="2"
                  inputmode="numeric"
                  @focus="pickerActiveUnit = 'minute'"
                  @blur="formatPickerMinuteOnBlur"
                  @input="handleMinuteInput"
                  @keydown="handleMinuteKeydown"
                />
              </div>
              <div class="timepicker-unit-label">Minute</div>
            </div>
          </div>

          <!-- Footer Actions -->
          <div class="timepicker-footer">
            <button type="button" class="btn-timepicker-action" @click="closeTimePickerModal">
              Cancel
            </button>
            <button type="button" class="btn-timepicker-action" @click="applyTimePicker">
              OK
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Dedicated Download Progress Modal (Exact Figma Design) -->
    <transition name="modal-fade">
      <div v-if="isExporting" class="download-progress-backdrop" @click.self="cancelDownload" @touchmove.self.prevent>
        <div class="download-progress-card">
          <!-- Animated Circular Spinning Loader -->
          <div class="download-spinner-wrap">
            <svg class="download-spinner" viewBox="0 0 48 48" width="46" height="46">
              <circle
                class="spinner-track"
                cx="24"
                cy="24"
                r="19"
                fill="none"
                stroke="#e2e8f0"
                stroke-width="4"
              />
              <circle
                class="spinner-arc"
                cx="24"
                cy="24"
                r="19"
                fill="none"
                stroke="#3b82f6"
                stroke-width="4"
                stroke-dasharray="119.38"
                stroke-dashoffset="89.5"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <!-- Title & Subtitle -->
          <h3 class="download-progress-title">Downloading...</h3>
          <p class="download-progress-subtitle">Preparing your Excel file (.xlsx)</p>

          <!-- Progress Bar -->
          <div class="download-bar-track">
            <div class="download-bar-fill" :style="{ width: exportProgress + '%' }"></div>
          </div>

          <!-- Percentage Indicator -->
          <div class="download-progress-percent">{{ exportProgress }}%</div>

          <!-- Horizontal Divider -->
          <div class="download-progress-divider"></div>

          <!-- Cancel Action -->
          <div class="download-progress-footer">
            <button type="button" class="btn-download-cancel" @click="cancelDownload">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Download Complete Modal (Exact Figma Specs: 400x343, radius 4px) -->
    <transition name="modal-fade">
      <div v-if="showCompleteModal" class="complete-modal-backdrop" @click.self="closeCompleteModal" @touchmove.self.prevent>
        <div class="complete-modal-card">
          <!-- Green Success Circle with Checkmark -->
          <div class="complete-icon-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M5.5 12.5L10 17L18.5 7.5"
                stroke="#ffffff"
                stroke-width="2.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <!-- Titles -->
          <h3 class="complete-title">Download Complete</h3>
          <p class="complete-subtitle">The file has been saved to your device</p>

          <!-- File Info Card -->
          <div class="complete-file-card">
            <div class="complete-file-icon">
              <svg width="24" height="30" viewBox="0 0 24 30" fill="none">
                <path
                  d="M2.5 0C1.11929 0 0 1.11929 0 2.5V27.5C0 28.8807 1.11929 30 2.5 30H21.5C22.8807 30 24 28.8807 24 27.5V8L16 0H2.5Z"
                  fill="#2E9350"
                />
                <path
                  d="M16 0V6.5C16 7.32843 16.6716 8 17.5 8H24L16 0Z"
                  fill="#88CA9E"
                />
                <path
                  d="M6.5 15L12.5 23M12.5 15L6.5 23"
                  stroke="#ffffff"
                  stroke-width="2.2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
            <div class="complete-file-info">
              <div class="complete-file-name" :title="downloadedFile.name">
                {{ downloadedFile.name || 'Mapping_Report.xlsx' }}
              </div>
              <div class="complete-file-meta">
                Excel &bull; {{ downloadedFile.size || '2.4 MB' }}
              </div>
            </div>
          </div>

          <!-- Open File Action Button -->
          <button type="button" class="btn-complete-open" @click="handleOpenFile">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
              <polyline points="15 3 21 3 21 9"></polyline>
              <line x1="10" y1="14" x2="21" y2="3"></line>
            </svg>
            <span>Open File</span>
          </button>

          <!-- Close Action Link -->
          <button type="button" class="btn-complete-close" @click="closeCompleteModal">
            Close
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';

const DEFAULT_PDC_NAME_MAP = {
  'DPDC-ASSY': 'DPDC ASSY',
  'DPDC-ASSY KRWG': 'DPDC ASSY KRWG',
  'DPDC-CIBINONG': 'PDC Cibinong',
  'DPDC-CIBITUNG': 'PDC Cibitung',
  'DPDC-KARAWANG': 'PDC Karawang',
  'DPDC-PST': 'PDC Sunter'
};

export default {
  name: 'ExportExcelModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Mapping Report'
    },
    availablePdcs: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      businessDate: false,
      startDate: '',
      endDate: '',
      showDatePickerModal: false,
      pickerBusinessDate: false,
      pickerStartDate: '',
      pickerEndDate: '',
      pickerCurrentYear: new Date().getFullYear(),
      pickerCurrentMonth: new Date().getMonth(),
      isSelectingRangeEnd: false,
      selectedShift: '',
      startTime: '00:00',
      endTime: '00:00',
      showTimePickerModal: false,
      timePickerTarget: 'start',
      pickerHour: '00',
      pickerMinute: '00',
      pickerActiveUnit: 'hour',
      selectedPdcs: [], // empty means All PDCs
      masterPdcs: [
        { pdc: 'DPDC-ASSY', name: 'DPDC ASSY' },
        { pdc: 'DPDC-ASSY KRWG', name: 'DPDC ASSY KRWG' },
        { pdc: 'DPDC-CIBINONG', name: 'PDC Cibinong' },
        { pdc: 'DPDC-CIBITUNG', name: 'PDC Cibitung' },
        { pdc: 'DPDC-KARAWANG', name: 'PDC Karawang' },
        { pdc: 'DPDC-PST', name: 'PDC Sunter' }
      ],
      pdcFetched: false,
      pdcDropdownOpen: false,
      pdcDropup: false,
      pdcMenuMaxHeight: null,
      pdcSearchQuery: '',
      isExporting: false,
      exportProgress: 0,
      exportInterval: null,
      showCompleteModal: false,
      downloadedRows: [],
      downloadedFile: {
        name: 'Mapping_Report.xlsx',
        size: '0 KB',
        blobUrl: null
      },
      toast: {
        show: false,
        title: '',
        message: '',
        type: 'success'
      },
      toastTimeout: null,
      isScrollLocked: false,
      originalBodyOverflow: '',
      originalHtmlOverflow: '',
      originalBodyPaddingRight: ''
    };
  },
  watch: {
    businessDate: {
      handler(newVal) {
        if (newVal) {
          this.selectedShift = '';
          this.startTime = '07:00';
          this.endTime = '06:59';
        }
      },
      immediate: true
    },
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.lockBodyScroll();
          this.ensureMasterPdcs();
        } else {
          this.unlockBodyScroll();
          this.pdcDropdownOpen = false;
          this.showCompleteModal = false;
          this.isExporting = false;
          if (this.exportInterval) {
            clearInterval(this.exportInterval);
            this.exportInterval = null;
          }
          this.resetForm();
        }
      }
    }
  },
  computed: {
    isDownloadDisabled() {
      return this.isExporting || !this.startDate;
    },
    formattedDateDisplay() {
      if (!this.startDate) return '';
      if (!this.endDate || this.startDate === this.endDate) {
        return moment(this.startDate).format('MMM DD, YYYY');
      }
      const startM = moment(this.startDate);
      const endM = moment(this.endDate);
      if (startM.year() === endM.year()) {
        return `${startM.format('MMM DD')} – ${endM.format('MMM DD, YYYY')}`;
      }
      return `${startM.format('MMM DD, YYYY')} – ${endM.format('MMM DD, YYYY')}`;
    },
    pickerFormattedTitle() {
      if (!this.pickerStartDate) return 'Select Date';
      if (!this.pickerEndDate || this.pickerStartDate === this.pickerEndDate) {
        return moment(this.pickerStartDate).format('MMM DD, YYYY');
      }
      const startM = moment(this.pickerStartDate);
      const endM = moment(this.pickerEndDate);
      if (startM.year() === endM.year()) {
        return `${startM.format('MMM DD')} – ${endM.format('MMM DD, YYYY')}`;
      }
      return `${startM.format('MMM DD, YYYY')} – ${endM.format('MMM DD, YYYY')}`;
    },
    pickerMonthYearText() {
      return moment({ year: this.pickerCurrentYear, month: this.pickerCurrentMonth }).format('MMMM YYYY');
    },
    daysInCurrentMonth() {
      return new Date(this.pickerCurrentYear, this.pickerCurrentMonth + 1, 0).getDate();
    },
    blankDaysBeforeMonth() {
      return new Date(this.pickerCurrentYear, this.pickerCurrentMonth, 1).getDay();
    },
    pdcList() {
      const source = (Array.isArray(this.availablePdcs) && this.availablePdcs.length > 0)
        ? this.availablePdcs
        : (this.masterPdcs && this.masterPdcs.length > 0
            ? this.masterPdcs
            : (this.$store && this.$store.getters && Array.isArray(this.$store.getters.getPdc)
                ? this.$store.getters.getPdc
                : []));

      return source
        .map(item => {
          if (typeof item === 'string') {
            return {
              pdc: item,
              name: DEFAULT_PDC_NAME_MAP[item] || item
            };
          }
          if (item && typeof item === 'object') {
            const code = item.pdc || item.loc_code || item.id || item.name || '';
            const label = item.name || DEFAULT_PDC_NAME_MAP[code] || code;
            return { pdc: code, name: label };
          }
          return null;
        })
        .filter(item => item && item.pdc);
    },
    filteredPdcs() {
      if (!this.pdcSearchQuery) return this.pdcList;
      const q = this.pdcSearchQuery.toLowerCase();
      return this.pdcList.filter(item =>
        item.name.toLowerCase().includes(q) || item.pdc.toLowerCase().includes(q)
      );
    },
    isAllPdcsSelected() {
      return this.selectedPdcs.length === 0 || this.selectedPdcs.length === this.pdcList.length;
    },
    pdcSummaryText() {
      if (this.selectedPdcs.length === 0) {
        return 'Select PDC';
      }
      if (this.selectedPdcs.length === 1) {
        return this.getPdcName(this.selectedPdcs[0]);
      }
      return `${this.selectedPdcs.length} PDCs selected`;
    },
    pdcMenuStyle() {
      if (this.pdcMenuMaxHeight) {
        return { maxHeight: `${this.pdcMenuMaxHeight}px` };
      }
      return {};
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.onWindowResize);
    this.ensureMasterPdcs();
  },
  beforeDestroy() {
    this.unlockBodyScroll();
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.onWindowResize);
    if (this.exportInterval) {
      clearInterval(this.exportInterval);
      this.exportInterval = null;
    }
  },
  methods: {
    lockBodyScroll() {
      if (typeof document === 'undefined') return;
      if (this.isScrollLocked) return;

      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      this.originalBodyOverflow = document.body.style.overflow;
      this.originalHtmlOverflow = document.documentElement.style.overflow;
      this.originalBodyPaddingRight = document.body.style.paddingRight;

      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      this.isScrollLocked = true;
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined' || !this.isScrollLocked) return;

      document.body.style.overflow = this.originalBodyOverflow || '';
      document.documentElement.style.overflow = this.originalHtmlOverflow || '';
      document.body.style.paddingRight = this.originalBodyPaddingRight || '';
      this.isScrollLocked = false;
    },
    openDatePickerModal() {
      this.pickerBusinessDate = this.businessDate;
      this.pickerStartDate = this.startDate;
      this.pickerEndDate = this.endDate;
      if (this.startDate) {
        const m = moment(this.startDate);
        this.pickerCurrentYear = m.year();
        this.pickerCurrentMonth = m.month();
      } else {
        const now = moment();
        this.pickerCurrentYear = now.year();
        this.pickerCurrentMonth = now.month();
      }
      this.isSelectingRangeEnd = false;
      this.showDatePickerModal = true;
    },
    closeDatePickerModal() {
      this.showDatePickerModal = false;
    },
    applyDatePicker() {
      if (!this.pickerStartDate) return;
      this.businessDate = this.pickerBusinessDate;
      this.startDate = this.pickerStartDate;
      this.endDate = this.pickerEndDate || this.pickerStartDate;
      if (this.businessDate) {
        this.selectedShift = '';
        this.startTime = '07:00';
        this.endTime = '06:59';
      }
      this.showDatePickerModal = false;
    },
    togglePickerBusinessDate() {
      this.pickerBusinessDate = !this.pickerBusinessDate;
      if (this.pickerBusinessDate && this.pickerStartDate) {
        this.pickerEndDate = moment(this.pickerStartDate).add(1, 'days').format('YYYY-MM-DD');
        this.isSelectingRangeEnd = false;
      }
    },
    prevMonth() {
      if (this.pickerCurrentMonth === 0) {
        this.pickerCurrentMonth = 11;
        this.pickerCurrentYear--;
      } else {
        this.pickerCurrentMonth--;
      }
    },
    nextMonth() {
      if (this.pickerCurrentMonth === 11) {
        this.pickerCurrentMonth = 0;
        this.pickerCurrentYear++;
      } else {
        this.pickerCurrentMonth++;
      }
    },
    getDayCellClass(day) {
      const dateStr = moment({
        year: this.pickerCurrentYear,
        month: this.pickerCurrentMonth,
        day: day
      }).format('YYYY-MM-DD');

      const isStart = this.pickerStartDate === dateStr;
      const isEnd = this.pickerEndDate === dateStr;
      const isBetween =
        this.pickerStartDate &&
        this.pickerEndDate &&
        moment(dateStr).isAfter(this.pickerStartDate) &&
        moment(dateStr).isBefore(this.pickerEndDate);

      return {
        'start-date': isStart,
        'end-date': isEnd,
        'single-selected': isStart && (!this.pickerEndDate || this.pickerStartDate === this.pickerEndDate),
        'range-date': isBetween
      };
    },
    onDayClick(day) {
      const dateStr = moment({
        year: this.pickerCurrentYear,
        month: this.pickerCurrentMonth,
        day: day
      }).format('YYYY-MM-DD');

      if (this.pickerBusinessDate) {
        // Business Date ON -> Start is chosen date, End is automatically H+1
        this.pickerStartDate = dateStr;
        this.pickerEndDate = moment(dateStr).add(1, 'days').format('YYYY-MM-DD');
      } else {
        // Business Date OFF -> Click start date, then click end date
        if (!this.isSelectingRangeEnd || !this.pickerStartDate) {
          this.pickerStartDate = dateStr;
          this.pickerEndDate = null;
          this.isSelectingRangeEnd = true;
        } else {
          if (moment(dateStr).isBefore(this.pickerStartDate)) {
            this.pickerEndDate = this.pickerStartDate;
            this.pickerStartDate = dateStr;
          } else {
            this.pickerEndDate = dateStr;
          }
          this.isSelectingRangeEnd = false;
        }
      }
    },
    selectShift(shift) {
      if (this.businessDate) return;
      this.selectedShift = shift;
      if (shift === 'morning') {
        this.startTime = '07:00';
        this.endTime = '19:59';
      } else if (shift === 'night') {
        this.startTime = '20:00';
        this.endTime = '06:59';
      } else if (shift === 'custom') {
        if (!this.startTime || this.startTime === '00:00') {
          this.startTime = '07:00';
        }
        if (!this.endTime || this.endTime === '00:00') {
          this.endTime = '19:59';
        }
        this.openTimePicker('start');
      }
    },
    onTimeFieldClick(target) {
      if (this.businessDate || this.selectedShift !== 'custom') {
        return;
      }
      this.openTimePicker(target);
    },
    openTimePicker(target) {
      if (this.businessDate || this.selectedShift !== 'custom') {
        return;
      }
      this.timePickerTarget = target;
      const currentVal = target === 'start' ? this.startTime : this.endTime;
      const parts = (currentVal || '00:00').split(':');
      this.pickerHour = parts[0] ? parts[0].padStart(2, '0') : '00';
      this.pickerMinute = parts[1] ? parts[1].padStart(2, '0') : '00';
      this.pickerActiveUnit = 'hour';
      this.showTimePickerModal = true;
      this.$nextTick(() => {
        this.focusHourInput();
      });
    },
    closeTimePickerModal() {
      this.showTimePickerModal = false;
    },
    applyTimePicker() {
      this.formatPickerHourOnBlur();
      this.formatPickerMinuteOnBlur();
      const h = this.pickerHour.padStart(2, '0');
      const m = this.pickerMinute.padStart(2, '0');
      const formatted = `${h}:${m}`;
      if (this.timePickerTarget === 'start') {
        this.startTime = formatted;
      } else {
        this.endTime = formatted;
      }
      this.selectedShift = 'custom';
      this.showTimePickerModal = false;
    },
    focusHourInput() {
      this.pickerActiveUnit = 'hour';
      this.$nextTick(() => {
        if (this.$refs.timeHourInput) {
          this.$refs.timeHourInput.focus();
          this.$refs.timeHourInput.select();
        }
      });
    },
    focusMinuteInput() {
      this.pickerActiveUnit = 'minute';
      this.$nextTick(() => {
        if (this.$refs.timeMinuteInput) {
          this.$refs.timeMinuteInput.focus();
          this.$refs.timeMinuteInput.select();
        }
      });
    },
    handleHourInput(e) {
      let val = e.target.value.replace(/\D/g, '');
      if (val.length > 2) val = val.slice(-2);

      if (val.length === 1 && parseInt(val, 10) >= 3) {
        this.pickerHour = '0' + val;
        this.focusMinuteInput();
        return;
      }

      if (val.length === 2) {
        let num = parseInt(val, 10);
        if (num > 23) num = 23;
        this.pickerHour = String(num).padStart(2, '0');
        this.focusMinuteInput();
        return;
      }

      this.pickerHour = val;
    },
    handleHourKeydown(e) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        let num = (parseInt(this.pickerHour, 10) || 0) + 1;
        if (num > 23) num = 0;
        this.pickerHour = String(num).padStart(2, '0');
        this.$nextTick(() => this.$refs.timeHourInput && this.$refs.timeHourInput.select());
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        let num = (parseInt(this.pickerHour, 10) || 0) - 1;
        if (num < 0) num = 23;
        this.pickerHour = String(num).padStart(2, '0');
        this.$nextTick(() => this.$refs.timeHourInput && this.$refs.timeHourInput.select());
      } else if (e.key === 'ArrowRight' || e.key === 'Tab' || e.key === ':') {
        if (e.key !== 'Tab') e.preventDefault();
        this.focusMinuteInput();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.applyTimePicker();
      }
    },
    formatPickerHourOnBlur() {
      if (!this.pickerHour) {
        this.pickerHour = '00';
      } else {
        let num = parseInt(this.pickerHour, 10) || 0;
        if (num > 23) num = 23;
        if (num < 0) num = 0;
        this.pickerHour = String(num).padStart(2, '0');
      }
    },
    handleMinuteInput(e) {
      let val = e.target.value.replace(/\D/g, '');
      if (val.length > 2) val = val.slice(-2);

      if (val.length === 1 && parseInt(val, 10) >= 6) {
        this.pickerMinute = '0' + val;
        return;
      }

      if (val.length === 2) {
        let num = parseInt(val, 10);
        if (num > 59) num = 59;
        this.pickerMinute = String(num).padStart(2, '0');
        return;
      }

      this.pickerMinute = val;
    },
    handleMinuteKeydown(e) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        let num = (parseInt(this.pickerMinute, 10) || 0) + 1;
        if (num > 59) num = 0;
        this.pickerMinute = String(num).padStart(2, '0');
        this.$nextTick(() => this.$refs.timeMinuteInput && this.$refs.timeMinuteInput.select());
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        let num = (parseInt(this.pickerMinute, 10) || 0) - 1;
        if (num < 0) num = 59;
        this.pickerMinute = String(num).padStart(2, '0');
        this.$nextTick(() => this.$refs.timeMinuteInput && this.$refs.timeMinuteInput.select());
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.focusHourInput();
      } else if (e.key === 'Backspace' && (!this.pickerMinute || this.pickerMinute.length === 0)) {
        this.focusHourInput();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.applyTimePicker();
      }
    },
    formatPickerMinuteOnBlur() {
      if (!this.pickerMinute) {
        this.pickerMinute = '00';
      } else {
        let num = parseInt(this.pickerMinute, 10) || 0;
        if (num > 59) num = 59;
        if (num < 0) num = 0;
        this.pickerMinute = String(num).padStart(2, '0');
      }
    },
    handleWheel(e, unit) {
      e.preventDefault();
      if (unit === 'hour') {
        let num = (parseInt(this.pickerHour, 10) || 0) + (e.deltaY < 0 ? 1 : -1);
        if (num > 23) num = 0;
        if (num < 0) num = 23;
        this.pickerHour = String(num).padStart(2, '0');
      } else {
        let num = (parseInt(this.pickerMinute, 10) || 0) + (e.deltaY < 0 ? 1 : -1);
        if (num > 59) num = 0;
        if (num < 0) num = 59;
        this.pickerMinute = String(num).padStart(2, '0');
      }
    },
    togglePdcDropdown() {
      this.pdcDropdownOpen = !this.pdcDropdownOpen;
      if (this.pdcDropdownOpen) {
        this.$nextTick(() => {
          this.calculateDropdownPlacement();
        });
      }
    },
    calculateDropdownPlacement() {
      if (!this.$refs.pdcDropdownWrapper) return;
      const rect = this.$refs.pdcDropdownWrapper.getBoundingClientRect();
      const defaultMenuHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - 16;
      const spaceAbove = rect.top - 16;

      // If space below is not enough for the dropdown, and space above has more room, open upwards (dropup)
      if (spaceBelow < defaultMenuHeight && spaceAbove > spaceBelow) {
        this.pdcDropup = true;
        this.pdcMenuMaxHeight = Math.max(160, Math.min(defaultMenuHeight, spaceAbove));
      } else {
        this.pdcDropup = false;
        this.pdcMenuMaxHeight = Math.max(160, Math.min(defaultMenuHeight, Math.max(spaceBelow, 160)));
      }
    },
    onWindowResize() {
      if (this.pdcDropdownOpen) {
        this.calculateDropdownPlacement();
      }
    },
    applyPdcSelection() {
      this.pdcDropdownOpen = false;
    },
    togglePdc(pdc) {
      const idx = this.selectedPdcs.indexOf(pdc);
      if (idx > -1) {
        this.selectedPdcs.splice(idx, 1);
      } else {
        this.selectedPdcs.push(pdc);
      }
    },
    getPdcName(pdcCode) {
      const found = this.pdcList.find(item => item.pdc === pdcCode);
      if (found && found.name) return found.name;
      return DEFAULT_PDC_NAME_MAP[pdcCode] || pdcCode;
    },
    ensureMasterPdcs() {
      if (this.pdcFetched || !this.$http) return;
      this.$http
        .get('/api/master/pdc?pagination=false&by=name&order=ASC')
        .then(resp => {
          const rawData = resp && resp.data && Array.isArray(resp.data.data) ? resp.data.data : [];
          const mapped = rawData
            .map(item => {
              if (typeof item === 'string') {
                return { pdc: item, name: DEFAULT_PDC_NAME_MAP[item] || item };
              }
              if (item && typeof item === 'object') {
                const code = item.pdc || item.loc_code || '';
                const name = item.name || DEFAULT_PDC_NAME_MAP[code] || code;
                return code ? { pdc: code, name } : null;
              }
              return null;
            })
            .filter(Boolean);
          if (mapped.length > 0) {
            this.masterPdcs = mapped;
            this.pdcFetched = true;
            if (this.$store && this.$store.dispatch && (!this.$store.getters.getPdc || this.$store.getters.getPdc.length === 0)) {
              this.$store.dispatch('setPdc', mapped.map(x => x.pdc));
            }
          }
        })
        .catch(() => {});
    },
    toggleAllPdcs() {
      if (this.isAllPdcsSelected) {
        this.selectedPdcs = [];
      } else {
        this.selectedPdcs = this.pdcList.map(item => item.pdc);
      }
    },
    removePdc(pdc) {
      const idx = this.selectedPdcs.indexOf(pdc);
      if (idx > -1) {
        this.selectedPdcs.splice(idx, 1);
      }
    },
    handleDocumentClick(e) {
      if (!this.pdcDropdownOpen) return;
      if (this.$refs.pdcDropdownWrapper && !this.$refs.pdcDropdownWrapper.contains(e.target)) {
        this.pdcDropdownOpen = false;
      }
    },
    cancelDownload() {
      if (this.exportInterval) {
        clearInterval(this.exportInterval);
        this.exportInterval = null;
      }
      this.isExporting = false;
      this.exportProgress = 0;
    },
    handleOpenFile() {
      if (!this.downloadedFile) return;

      const rows = this.downloadedRows && this.downloadedRows.length > 0 ? this.downloadedRows : [];
      const headers = ['QRCode', 'Frame Number', 'Type', 'Manufacture Year', 'Location', 'Shift', 'Date Mapping', 'Status'];
      const colLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

      const tableRowsHtml = rows.map((row, idx) => {
        const cells = headers.map(h => `<td>${row[h] || ''}</td>`).join('');
        return `<tr><td class="row-num">${idx + 1}</td>${cells}</tr>`;
      }).join('');

      const colHeadersHtml = headers.map((h, i) => `<th><div class="col-letter">${colLetters[i] || ''}</div><div class="col-name">${h}</div></th>`).join('');

      const newWin = window.open('', '_blank');
      if (newWin) {
        newWin.document.write(`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${this.downloadedFile.name}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif; background: #ffffff; color: #1e293b; height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
    .excel-topbar { background: #107c41; color: #ffffff; height: 50px; display: flex; align-items: center; justify-content: space-between; padding: 0 16px; flex-shrink: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .excel-title-group { display: flex; align-items: center; gap: 10px; }
    .excel-icon { width: 28px; height: 28px; background: #ffffff; color: #107c41; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 16px; font-family: Arial, sans-serif; }
    .excel-filename { font-weight: 600; font-size: 15px; }
    .excel-badge { background: rgba(255, 255, 255, 0.2); font-size: 11px; padding: 2px 8px; border-radius: 10px; }
    .excel-actions { display: flex; gap: 8px; }
    .btn-action { background: rgba(255, 255, 255, 0.15); border: 1px solid rgba(255, 255, 255, 0.3); color: #ffffff; padding: 6px 14px; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; transition: background 0.15s; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; }
    .btn-action:hover { background: rgba(255, 255, 255, 0.3); }
    .excel-subbar { background: #f8fafc; border-bottom: 1px solid #e2e8f0; padding: 8px 16px; font-size: 12.5px; color: #64748b; display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; }
    .excel-table-container { flex: 1; overflow: auto; background: #ffffff; position: relative; }
    table { border-collapse: collapse; min-width: 100%; font-size: 13px; font-family: Calibri, Aptos, "Segoe UI", sans-serif; }
    th { background: #f8fafc; border: 1px solid #cbd5e1; font-weight: 600; color: #334155; text-align: left; position: sticky; top: 0; z-index: 10; padding: 8px 12px; }
    th.corner-cell { width: 44px; min-width: 44px; max-width: 44px; text-align: center; background: #eaedf2; z-index: 20; left: 0; }
    .col-letter { font-size: 10px; color: #94a3b8; text-align: center; margin-bottom: 2px; }
    .col-name { font-size: 12.5px; font-weight: 600; }
    td { border: 1px solid #e2e8f0; padding: 8px 12px; white-space: nowrap; color: #1e293b; }
    td.row-num { background: #f8fafc; text-align: center; color: #64748b; font-size: 11px; font-weight: 600; width: 44px; min-width: 44px; max-width: 44px; position: sticky; left: 0; border-right: 1px solid #cbd5e1; z-index: 5; }
    tr:nth-child(even) td:not(.row-num) { background: #fafbfc; }
    tr:hover td:not(.row-num) { background: #f0f7ff; }
    .excel-statusbar { background: #107c41; color: #ffffff; height: 28px; padding: 0 16px; font-size: 11px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
    .sheet-tab { background: #ffffff; color: #107c41; padding: 5px 16px 2px 16px; font-weight: 600; border-radius: 4px 4px 0 0; font-size: 12px; display: inline-block; }
  </style>
</head>
<body>
  <div class="excel-topbar">
    <div class="excel-title-group">
      <div class="excel-icon">X</div>
      <span class="excel-filename">${this.downloadedFile.name}</span>
      <span class="excel-badge">Spreadsheet View</span>
    </div>
    <div class="excel-actions">
      <button class="btn-action" onclick="window.print()">🖨️ Print</button>
      <a class="btn-action" href="${this.downloadedFile.blobUrl}" download="${this.downloadedFile.name}">📥 Download .xlsx</a>
      <button class="btn-action" onclick="window.close()">✕ Close</button>
    </div>
  </div>
  <div class="excel-subbar">
    <div><strong>Sheet:</strong> Mapping Report &nbsp;•&nbsp; <strong>Total Rows:</strong> ${rows.length} records</div>
    <div>Generated from DSO Dashboard</div>
  </div>
  <div class="excel-table-container">
    <table>
      <thead>
        <tr>
          <th class="corner-cell"></th>
          ${colHeadersHtml}
        </tr>
      </thead>
      <tbody>
        ${tableRowsHtml}
      </tbody>
    </table>
  </div>
  <div class="excel-statusbar">
    <div style="display:flex; align-items:center; gap:8px;">
      <span class="sheet-tab">Mapping Report</span>
      <span>Ready</span>
    </div>
    <div>100% Zoom</div>
  </div>
</body>
</html>`);
        newWin.document.close();
      }
    },
    closeCompleteModal() {
      this.showCompleteModal = false;
      this.pdcDropdownOpen = false;
      this.showDatePickerModal = false;
      this.showTimePickerModal = false;
      this.$emit('close');
      this.$emit('update:visible', false);
    },
    handleCancel() {
      if (this.isExporting) return;
      this.pdcDropdownOpen = false;
      this.showDatePickerModal = false;
      this.$emit('close');
      this.$emit('update:visible', false);
    },
    resetForm() {
      this.startDate = '';
      this.endDate = '';
      this.businessDate = false;
      this.selectedShift = '';
      this.startTime = '00:00';
      this.endTime = '00:00';
      this.selectedPdcs = [];
      this.pdcSearchQuery = '';
      this.pickerStartDate = '';
      this.pickerEndDate = '';
      this.pickerBusinessDate = false;
      this.isSelectingRangeEnd = false;
    },
    async handleDownload() {
      if (this.isExporting || !this.startDate) return;

      // Client-side validations
      if (!this.businessDate && this.startDate && this.endDate) {
        if (moment(this.endDate).isBefore(moment(this.startDate))) {
          this.showToast('Validation Error', 'end_date harus setelah atau sama dengan start_date.', 'error');
          return;
        }
      }

      if (this.selectedShift === 'custom' && this.startTime === this.endTime) {
        this.showToast(
          'Validation Error',
          'start_time dan end_time tidak boleh sama untuk shift custom (ambigu antara 0 atau 24 jam).',
          'error'
        );
        return;
      }

      // Build parameters according to UR04 API Reference & Backend compatibility
      const params = {
        business_date: this.businessDate ? 'true' : 'false'
      };

      if (this.businessDate) {
        params.date = this.startDate || moment().format('YYYY-MM-DD');
        // Legacy backend compatibility (requires start_date & end_date):
        params.start_date = this.startDate || moment().format('YYYY-MM-DD');
        params.end_date = this.endDate || moment(this.startDate || undefined).add(1, 'days').format('YYYY-MM-DD');
      } else {
        params.start_date = this.startDate || moment().format('YYYY-MM-DD');
        params.end_date = this.endDate || this.startDate || moment().format('YYYY-MM-DD');
      }

      // Determine hours based on shift
      let startHour = '00:00:00';
      let endHour = '23:59:59';

      if (this.businessDate) {
        startHour = '07:00:00';
        endHour = '06:59:59';
      } else if (this.selectedShift === 'morning') {
        params.shift = 'morning';
        startHour = '07:00:00';
        endHour = '19:59:59';
      } else if (this.selectedShift === 'night') {
        params.shift = 'night';
        startHour = '20:00:00';
        endHour = '06:59:59';
      } else if (this.selectedShift === 'custom') {
        params.shift = 'custom';
        startHour = this.startTime ? `${this.startTime}:00` : '07:00:00';
        endHour = this.endTime ? (this.endTime.endsWith('59') ? `${this.endTime}:59` : `${this.endTime}:00`) : '19:59:59';
      } else if (this.startTime && this.endTime && this.startTime !== '00:00' && this.endTime !== '00:00') {
        startHour = `${this.startTime}:00`;
        endHour = this.endTime.endsWith('59') ? `${this.endTime}:59` : `${this.endTime}:00`;
      }

      // Backend requires start_hour and end_hour whenever start_date is provided
      params.start_hour = startHour;
      params.end_hour = endHour;

      // UR04 API spec fields
      params.start_time = startHour.slice(0, 5);
      params.end_time = endHour.slice(0, 5);

      if (this.selectedPdcs.length > 0) {
        params.pdc = this.selectedPdcs.join(',');
      }

      // Start exporting state & progress simulation
      this.isExporting = true;
      this.exportProgress = 10;

      if (this.exportInterval) {
        clearInterval(this.exportInterval);
      }

      this.exportInterval = setInterval(() => {
        if (this.exportProgress < 85) {
          const step = Math.floor(Math.random() * 8) + 4;
          this.exportProgress = Math.min(85, this.exportProgress + step);
        }
      }, 150);

      try {
        const response = await this.$http.get('/api/vehicle/map/excel', { params });

        if (this.exportInterval) {
          clearInterval(this.exportInterval);
          this.exportInterval = null;
        }

        if (response && response.data) {
          const resData = response.data;
          const meta = resData.meta || {};

          // Check if response returned an API-level error code (e.g. code: 40)
          if (resData.code && resData.code !== 200) {
            this.cancelDownload();
            this.showToast('Export Error', resData.message || 'Failed to export report.', 'error');
            return;
          }

          // Check if total records exceed limit (§2.2 & §2.3)
          if (meta.exceeds_limit) {
            this.cancelDownload();
            this.showToast(
              'Limit Exceeded',
              `Data total (${meta.total} rows) melebihi batas maksimal ${meta.max_rows || 65535} baris. Mohon persempit filter tanggal atau PDC Anda.`,
              'error'
            );
            return;
          }

          this.exportProgress = 100;
          setTimeout(() => {
            this.processExportData(resData.data || []);
          }, 350);
        }
      } catch (error) {
        if (this.exportInterval) {
          clearInterval(this.exportInterval);
          this.exportInterval = null;
        }
        this.cancelDownload();

        let errorMsg = 'Failed to export report. Please try again.';
        if (error.response && error.response.data) {
          const errData = error.response.data;
          if (typeof errData.data === 'string') {
            errorMsg = errData.data;
          } else if (Array.isArray(errData.data) && errData.data.length > 0 && errData.data[0].message) {
            errorMsg = errData.data[0].message;
          } else if (errData.message) {
            errorMsg = errData.message;
          }
        } else if (error.message) {
          errorMsg = error.message;
        }

        this.showToast('Export Error', errorMsg, 'error');
      }
    },
    processExportData(dataList) {
      // Build a lookup from visible table items if available in parent component
      const tableLocationMap = {};
      const parentItems = (this.$parent && Array.isArray(this.$parent.items)) 
        ? this.$parent.items 
        : ((this.$parent && this.$parent.$children) 
            ? (this.$parent.$children.find(c => Array.isArray(c.items)) || {}).items 
            : null);
      if (Array.isArray(parentItems)) {
        parentItems.forEach(it => {
          if (it.frame_no && it.location) tableLocationMap[it.frame_no] = it.location;
          if (it.qrcode && it.location) tableLocationMap[it.qrcode] = it.location;
        });
      }

      // Map columns according to UR04 API Reference §2.2
      const rows = (dataList || []).map(row => {
        // 1. Date / Scan Date handling (supports date, date_mapping, scan_date, value_date)
        const rawDate = row.date_mapping || row.date || row.scan_date || row.value_date || '';
        let formattedDate = '';
        if (rawDate) {
          if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(rawDate)) {
            formattedDate = rawDate;
          } else {
            const tzoffset = (new Date()).getTimezoneOffset() * 60000;
            const localDate = new Date(new Date(rawDate).getTime() - tzoffset);
            if (!isNaN(localDate.getTime())) {
              formattedDate = localDate.toISOString().slice(0, 19).replace('T', ' ');
            } else if (moment(rawDate).isValid()) {
              formattedDate = moment(rawDate).format('YYYY-MM-DD HH:mm:ss');
            } else {
              formattedDate = String(rawDate);
            }
          }
        }

        // 2. Shift handling: use row.shift if provided, or determine from scan date
        let shift = row.shift || '';
        if (!shift && formattedDate) {
          const timePart = formattedDate.includes(' ') ? formattedDate.split(' ')[1] : '';
          const hhmm = timePart ? timePart.slice(0, 5) : '';
          if (hhmm) {
            if (hhmm >= '07:00' && hhmm < '20:00') {
              shift = 'Morning';
            } else {
              shift = 'Night';
            }
          }
        }
        if (!shift && (this.selectedShift === 'morning' || this.selectedShift === 'night')) {
          shift = this.selectedShift === 'morning' ? 'Morning' : 'Night';
        }

        // 3. Location handling: supports row.location, current_loc_code, loc_code, table lookup, or single filter
        let location = row.location || row.current_loc_code || row.loc_code || row.ori_loc_code || '';
        if (!location && row.frame_no && tableLocationMap[row.frame_no]) {
          location = tableLocationMap[row.frame_no];
        }
        if (!location && row.qrcode && tableLocationMap[row.qrcode]) {
          location = tableLocationMap[row.qrcode];
        }
        if (!location && this.selectedPdcs && this.selectedPdcs.length === 1) {
          location = this.selectedPdcs[0];
        }

        // 4. Type handling: do not force 'Unknown' if empty or null
        const type = (row.type && row.type !== 'Unknown' && row.type !== 'unknown') ? row.type : (row.type || '');

        return {
          'QRCode': row.qrcode || '',
          'Frame Number': row.frame_no || '',
          'Type': type,
          'Manufacture Year': row.manufacture_year != null ? row.manufacture_year : '',
          'Location': location,
          'Shift': shift,
          'Date Mapping': formattedDate,
          'Status': row.status_label || row.status || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);

      const colWidths = [
        { wch: 24 },
        { wch: 24 },
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 16 },
        { wch: 22 },
        { wch: 18 }
      ];
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Mapping Report');

      // Filename according to [1.6.10]:
      // 1. Single date: Mapping_Report_YYYY-MM-DD.xlsx
      // 2. Range date: Mapping_Report_YYYY-MM-DD-YYYY-MM-DD.xlsx
      let startDate = this.startDate || moment().format('YYYY-MM-DD');
      let endDate = this.endDate || startDate;
      if (startDate > endDate) {
        const temp = startDate;
        startDate = endDate;
        endDate = temp;
      }
      const isSingleDate = this.businessDate || !this.endDate || startDate === endDate;
      const dateTag = isSingleDate ? startDate : `${startDate}-${endDate}`;
      const filename = `Mapping_Report_${dateTag}.xlsx`;

      // Generate .xlsx file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const blobUrl = window.URL.createObjectURL(blob);

      // Trigger automatic browser download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Format size
      const sizeStr = blob.size > 1024 * 1024 
        ? (blob.size / (1024 * 1024)).toFixed(1) + ' MB' 
        : (blob.size / 1024).toFixed(1) + ' KB';

      // Store file info for Complete modal & Viewer
      this.downloadedRows = rows;
      this.downloadedFile = {
        name: filename,
        size: sizeStr,
        blobUrl: blobUrl
      };

      if (this.exportInterval) {
        clearInterval(this.exportInterval);
        this.exportInterval = null;
      }
      this.isExporting = false;
      this.exportProgress = 0;

      // Show complete modal
      this.showCompleteModal = true;
    },
    showToast(title, message, type = 'success') {
      this.toast = {
        show: true,
        title,
        message,
        type
      };
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        this.toast.show = false;
      }, 5000);
    }
  }
};
</script>

<style scoped>
/* Main Modal Overlay Backdrop */
.export-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}

/* Main Modal Card Container (Dynamic Responsive Dimensions) */
.export-modal-card {
  margin: auto;
  background: #ffffff;
  border-radius: 4px;
  width: 560px;
  max-width: calc(100vw - 32px);
  min-height: auto;
  height: auto;
  padding: 26px 24px 22px 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
  position: relative;
  box-sizing: border-box;
  opacity: 1;
  transform: rotate(0deg);
  display: flex;
  flex-direction: column;
}

/* Header */
.export-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.btn-modal-close {
  background: none;
  border: none;
  color: #64748b;
  font-size: 15px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;
  outline: none;
}

.btn-modal-close:hover {
  color: #0f172a;
}

.modal-subtitle {
  font-size: 13.5px;
  color: #64748b;
  margin: 0 0 18px 0;
  line-height: 1.4;
}

/* Form Groups */
.form-group-custom {
  margin-bottom: 16px;
  position: relative;
}

.field-label {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.label-optional {
  font-weight: 400;
  color: #64748b;
  font-size: 13px;
}

.sub-label {
  display: block;
  font-size: 12.5px;
  font-weight: 400;
  color: #64748b;
  margin-bottom: 6px;
}

/* Date Box (Triggers Date Picker dialog) */
.date-single-box {
  height: 38px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.date-single-box:hover {
  border-color: #94a3b8;
}

.date-display-text {
  font-size: 13.5px;
  font-weight: 400;
  color: #1e293b;
}

.date-display-text.placeholder-mode {
  color: #64748b;
}

.date-icon-right {
  color: #1e293b;
  font-size: 14px;
}

/* Shift Selector Chips */
.shift-pills-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.shift-pill {
  flex: 0 0 auto;
  width: auto;
  height: 32px;
  background: #eaedf2;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 14px;
  font-size: 13px;
  color: #5a6a85;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

.shift-pill:hover {
  background: #e2e8f0;
}

.shift-pill.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #ffffff;
  font-weight: 500;
}

.shift-pill.active:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.shift-pill:disabled,
.shift-pill.disabled {
  background: #eaedf2;
  border: 1px solid #cbd5e1;
  color: #5a6a85;
  cursor: not-allowed;
  pointer-events: none;
}

.shift-pill:disabled:hover,
.shift-pill.disabled:hover {
  background: #eaedf2;
  border-color: #cbd5e1;
  color: #5a6a85;
}

.shift-check-icon {
  flex-shrink: 0;
}

/* Time Inputs Row */
.time-inputs-row {
  display: flex;
  gap: 12px;
}

.time-input-col {
  flex: 1;
}

.time-field-box {
  height: 38px;
  display: flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 12px;
  background: #eaedf2;
  cursor: default;
  transition: all 0.15s ease;
  user-select: none;
}

.time-field-box:hover {
  border-color: #cbd5e1;
}

.time-field-box.is-custom {
  background: #eaedf2;
  border-color: #2563eb;
  cursor: pointer;
}

.time-field-box.is-custom:hover {
  border-color: #1d4ed8;
}

.time-field-box.disabled {
  background: #eaedf2;
  border: 1px solid #cbd5e1;
  cursor: not-allowed;
  pointer-events: none;
}

.time-field-box.disabled:hover {
  border-color: #cbd5e1;
}

.time-field-box.disabled .time-display-val {
  color: #5a6a85;
}

.time-display-val {
  font-size: 13.5px;
  font-weight: 400;
  color: #5a6a85;
  flex: 1;
}

.time-field-box.is-custom .time-display-val {
  color: #1e293b;
}

.shift-hint {
  font-size: 11.5px;
  color: #8c9ba5;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

/* PDC Multi-Select Dropdown */
.pdc-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.pdc-dropdown-trigger {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 12px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s;
}

.pdc-dropdown-trigger:hover {
  border-color: #94a3b8;
}

.pdc-trigger-text {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: #1e293b;
}

.pdc-trigger-text.is-placeholder {
  color: #64748b;
}

.caret-icon {
  color: #64748b;
  font-size: 14px;
  transition: transform 0.2s;
}

.caret-icon.rotate {
  transform: rotate(180deg);
}

/* PDC Multi-Select Dropdown Menu */
.pdc-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-width: 100%;
  height: 250px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-width: 1px;
  border-radius: 4px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  z-index: 100;
  opacity: 1;
  transform: rotate(0deg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

/* Dropup mode: opens upwards when space below is tight */
.pdc-dropdown-menu.dropup-mode {
  top: auto;
  bottom: calc(100% + 4px);
  box-shadow: 0 -10px 25px rgba(0, 0, 0, 0.12);
}

.pdc-search-header {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
}

.pdc-search-input {
  width: 100%;
  height: 38px;
  background: #eaedf2;
  border: none;
  border-radius: 4px;
  padding: 0 14px;
  font-size: 13.5px;
  color: #1e293b;
  outline: none;
  box-sizing: border-box;
}

.pdc-search-input::placeholder {
  color: #94a3b8;
}

.pdc-options-list {
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pdc-option-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.pdc-checkbox-box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1.5px solid #64748b;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}

.pdc-checkbox-box.checked {
  background: #3b82f6;
  border-color: #3b82f6;
}

.pdc-check-icon {
  color: #ffffff;
  font-size: 9px;
}

.pdc-option-label {
  font-size: 14px;
  color: #1e293b;
  font-weight: 400;
}

.pdc-dropdown-footer {
  height: 52px;
  padding: 0 16px;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pdc-selected-count {
  font-size: 13px;
  color: #64748b;
}

.btn-pdc-apply {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  height: 34px;
  padding: 0 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  outline: none;
}

.btn-pdc-apply:hover {
  background: #2563eb;
}

/* PDC Chips */
.pdc-chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.pdc-chip {
  display: inline-flex;
  align-items: center;
  background: #eaf2fd;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 13.5px;
  font-weight: 400;
  color: #2563eb;
  transition: background 0.15s ease;
}

.pdc-chip:hover {
  background: #dbeafe;
}

.chip-remove {
  background: none;
  border: none;
  color: #2563eb;
  font-size: 15px;
  margin-left: 8px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}

.chip-remove:hover {
  color: #1d4ed8;
}

/* Excel Format Note */
.format-note {
  font-size: 12px;
  color: #8c9ba5;
  margin-top: 10px;
  margin-bottom: 14px;
  line-height: 1.35;
}

/* Progress Bar */
.progress-section {
  margin-bottom: 14px;
}

.progress-bar-bg {
  width: 100%;
  height: 5px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-bar-fill {
  height: 100%;
  background: #2563eb;
  transition: width 0.25s ease-out;
}

.progress-text {
  font-size: 11px;
  color: #2563eb;
  font-weight: 600;
  text-align: right;
}

/* Footer Action Buttons */
.modal-footer-actions {
  display: flex;
  gap: 12px;
  margin-top: 10px;
  padding-top: 12px;
}

.btn-modal-cancel {
  flex: 1;
  height: 40px;
  background: #ffffff;
  border: 1px solid #4b5563;
  border-radius: 4px;
  font-size: 13.5px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-modal-cancel:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #1f2937;
  color: #111827;
}

.btn-modal-download {
  flex: 1;
  height: 40px;
  background: #3b82f6;
  border: none;
  border-radius: 4px;
  font-size: 13.5px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.15s;
}

.btn-modal-download:hover:not(:disabled) {
  background: #2563eb;
}

.btn-modal-cancel:disabled,
.btn-modal-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================= */
/* Custom Date Picker Modal (Figma Design)   */
/* ========================================= */
.datepicker-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}

/* Custom Date Picker Modal Card (Dynamic Dimensions) */
.datepicker-modal-card {
  margin: auto;
  background: #ffffff;
  border-radius: 4px;
  width: 362px;
  max-width: 95vw;
  min-height: auto;
  height: auto;
  padding: 24px 22px 18px 22px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.16);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
  box-sizing: border-box;
  opacity: 1;
  transform: rotate(0deg);
  display: flex;
  flex-direction: column;
}

.picker-top-label {
  font-size: 13.5px;
  color: #1e293b;
  margin-bottom: 12px;
  font-weight: 600;
}

.picker-big-date {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  margin-bottom: 14px;
}

.picker-divider {
  border: none;
  border-top: 1px solid #f1f5f9;
  margin: 0 0 14px 0;
}

.picker-month-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.picker-month-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.picker-nav-arrows {
  display: flex;
  gap: 16px;
  align-items: center;
}

.btn-nav-arrow {
  background: none;
  border: none;
  padding: 2px 4px;
  font-size: 11px;
  color: #334155;
  cursor: pointer;
  border-radius: 2px;
  transition: color 0.15s;
}

.btn-nav-arrow:hover {
  color: #0f172a;
}

/* Business Date Checkbox Inside Datepicker */
.business-date-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  cursor: pointer;
  user-select: none;
}

.custom-checkbox-box {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  flex-shrink: 0;
  transition: all 0.15s;
}

.custom-checkbox-box.checked {
  background: #3b82f6;
  border-color: #3b82f6;
}

.checkbox-check-icon {
  color: #ffffff;
  font-size: 9px;
}

.business-date-text-col {
  flex: 1;
}

.business-date-title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.2;
}

.business-date-desc {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.3;
}

/* Days of Week Header */
.calendar-days-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 8px;
}

/* Calendar Days Grid */
.calendar-days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 2px;
}

.day-cell {
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  font-size: 13.5px;
  font-weight: 400;
  color: #1e293b;
}

.day-cell.blank {
  cursor: default;
}

.day-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.15s;
}

.day-cell:not(.blank):not(.start-date):not(.end-date):not(.range-date):hover .day-number {
  background: #f1f5f9;
}

/* Range Selected States (Matching Screenshot Blue Circles & Soft Blue Box) */
.day-cell.start-date .day-number,
.day-cell.end-date .day-number {
  background: #3b82f6;
  color: #ffffff;
  font-weight: 500;
  border-radius: 50%;
  width: 34px;
  height: 34px;
}

.day-cell.range-date .day-number {
  background: #eaf2fd;
  color: #2563eb;
  font-weight: 500;
  border-radius: 6px;
  width: 34px;
  height: 34px;
}

/* Hint Text */
.picker-hint-text {
  font-size: 11.5px;
  color: #64748b;
  margin-top: auto;
  padding-top: 14px;
  margin-bottom: 14px;
  line-height: 1.4;
}

/* Action Buttons (Cancel & OK) */
.datepicker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 18px;
  align-items: center;
}

.btn-picker-action {
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  padding: 4px 6px;
  transition: color 0.15s;
}

.btn-picker-action:hover:not(:disabled) {
  color: #1d4ed8;
}

.btn-picker-action:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ========================================= */
/* Custom Time Picker Modal (Figma Design)   */
/* ========================================= */
.timepicker-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 2010;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}

/* Custom Time Picker Modal Card (Figma Specs: 320x188, radius 4px) */
.timepicker-modal-card {
  margin: auto;
  background: #ffffff;
  border-radius: 4px;
  width: 320px;
  max-width: 95vw;
  height: 188px;
  min-height: 188px;
  padding: 16px 20px 14px 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
  box-sizing: border-box;
  opacity: 1;
  transform: rotate(0deg);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.timepicker-top-label {
  font-size: 13.5px;
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 0;
  line-height: 1.2;
}

.timepicker-body {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 0;
}

.timepicker-digit-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timepicker-digit-box {
  width: 64px;
  height: 60px;
  background: #eaedf2;
  border: 1.5px solid transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  overflow: hidden;
}

.timepicker-digit-box:hover {
  background: #e2e8f0;
}

.timepicker-digit-box.active {
  background: #eaf2fd;
  border-color: #3b82f6;
}

.timepicker-num-input {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  outline: none;
  text-align: center;
  font-size: 28px;
  font-weight: 500;
  color: #1e293b;
  caret-color: #3b82f6;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}

.timepicker-digit-box.active .timepicker-num-input {
  color: #2563eb;
  cursor: text;
}

.timepicker-colon {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 2px;
}

.timepicker-unit-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
  margin-top: 4px;
  text-align: center;
}

.timepicker-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 0;
}

.btn-timepicker-action {
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  padding: 4px 6px;
  transition: color 0.15s ease;
  outline: none;
}

.btn-timepicker-action:hover {
  color: #1d4ed8;
}

/* Toast Banner Styling */
.export-toast-banner {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 3000;
  background: #ffffff;
  border-left: 4px solid #10b981;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 420px;
}

.export-toast-banner.error {
  border-left-color: #ef4444;
}

.export-toast-banner.error .toast-icon {
  color: #ef4444;
}

.toast-icon {
  font-size: 24px;
  color: #10b981;
}

.toast-content {
  flex: 1;
}

.toast-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 12px;
  color: #475569;
}

.toast-close {
  background: none;
  border: none;
  font-size: 18px;
  color: #94a3b8;
  cursor: pointer;
}

.toast-close:hover {
  color: #334155;
}

/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

/* =========================================================
   Download Progress Modal Card (Exact Figma Specs & Styling)
   ========================================================= */
.download-progress-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 2020;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}

.download-progress-card {
  margin: auto;
  background: #ffffff;
  border-radius: 4px;
  width: 360px;
  max-width: 92vw;
  padding: 28px 24px 14px 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
  box-sizing: border-box;
  text-align: center;
}

.download-spinner-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 14px;
}

.download-spinner {
  animation: download-spin 1s linear infinite;
  transform-origin: center;
}

@keyframes download-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.download-progress-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  line-height: 1.25;
}

.download-progress-subtitle {
  font-size: 13.5px;
  font-weight: 400;
  color: #64748b;
  margin: 0 0 20px 0;
  line-height: 1.35;
}

.download-bar-track {
  width: 100%;
  height: 6px;
  background: #eaedf2;
  border-radius: 4px;
  overflow: hidden;
}

.download-bar-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 4px;
  transition: width 0.18s ease-out;
}

.download-progress-percent {
  font-size: 13.5px;
  font-weight: 600;
  color: #2563eb;
  margin-top: 8px;
  margin-bottom: 16px;
}

.download-progress-divider {
  width: calc(100% + 48px);
  margin-left: -24px;
  height: 1px;
  background: #eaedf2;
}

.download-progress-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
}

.btn-download-cancel {
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  padding: 6px 16px;
  outline: none;
  transition: color 0.15s ease;
}

.btn-download-cancel:hover {
  color: #1d4ed8;
}

/* =========================================================
   Download Complete Modal Card (Exact Figma Specs: 400x343, radius 4px)
   ========================================================= */
.complete-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 2030;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
}

.complete-modal-card {
  margin: auto;
  background: #ffffff;
  border-radius: 4px;
  width: 400px;
  height: 343px;
  max-width: 95vw;
  padding: 28px 24px 20px 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #212529;
  box-sizing: border-box;
  opacity: 1;
  transform: rotate(0deg);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.complete-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #2E9350;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.complete-title {
  font-size: 19px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 6px 0;
  line-height: 1.25;
}

.complete-subtitle {
  font-size: 13.5px;
  font-weight: 400;
  color: #6c757d;
  margin: 0 0 18px 0;
  line-height: 1.35;
}

.complete-file-card {
  width: 100%;
  height: 68px;
  background: #f6f8fa;
  border: 1px solid #d8dee4;
  border-radius: 4px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  box-sizing: border-box;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.complete-file-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.complete-file-info {
  flex: 1;
  min-width: 0;
}

.complete-file-name {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.complete-file-meta {
  font-size: 12.5px;
  font-weight: 400;
  color: #6c757d;
  margin-top: 4px;
  line-height: 1.2;
}

.btn-complete-open {
  width: 100%;
  height: 40px;
  background: #3D7EEA;
  border-radius: 4px;
  border: none;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background-color 0.15s ease;
  outline: none;
  font-family: inherit;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.btn-complete-open:hover {
  background: #2e6fd8;
}

.btn-complete-close {
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 400;
  color: #6c757d;
  cursor: pointer;
  padding: 2px 16px;
  outline: none;
  transition: color 0.15s ease;
  font-family: inherit;
  margin-top: auto;
}

.btn-complete-close:hover {
  color: #212529;
}

/* =========================================================
   Responsive & Zoom Adaptations (Preserves Visibility on Zoom)
   ========================================================= */
@media (max-height: 760px), (max-width: 600px) {
  .export-modal-backdrop {
    padding: 16px 12px;
    align-items: center;
  }

  .export-modal-card {
    padding: 18px 20px 16px 20px;
    margin: auto;
    max-width: 100%;
  }

  .export-modal-header {
    margin-bottom: 8px;
  }

  .modal-title {
    font-size: 16.5px;
  }

  .modal-subtitle {
    margin-bottom: 10px;
    font-size: 12.5px;
  }

  .form-group-custom {
    margin-bottom: 10px;
  }

  .field-label {
    margin-bottom: 4px;
    font-size: 12.5px;
  }

  .sub-label {
    margin-bottom: 4px;
    font-size: 11.5px;
  }

  .date-single-box {
    height: 34px;
  }

  .shift-pills-row {
    margin-bottom: 8px;
  }

  .shift-pill {
    height: 32px;
    padding: 0 12px;
    font-size: 12.5px;
  }

  .time-field-box {
    height: 34px;
  }

  .shift-hint {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.25;
  }

  .pdc-dropdown-trigger {
    height: 34px;
  }

  .pdc-dropdown-menu {
    height: 230px;
  }

  .format-note {
    margin-top: 8px;
    margin-bottom: 10px;
    font-size: 11.5px;
  }

  .modal-footer-actions {
    margin-top: 8px;
    padding-top: 8px;
  }

  .btn-modal-cancel,
  .btn-modal-download {
    height: 36px;
    font-size: 13px;
  }

  /* Date Picker Compact */
  .datepicker-modal-backdrop {
    padding: 16px 12px;
    align-items: center;
  }

  .datepicker-modal-card {
    padding: 16px 16px 14px 16px;
    margin: auto;
  }

  .calendar-grid {
    gap: 2px;
  }

  .calendar-day-cell {
    height: 30px;
    font-size: 12px;
  }
}

@media (max-height: 560px) {
  .export-modal-card {
    padding: 12px 14px 12px 14px;
  }

  .pdc-dropdown-menu {
    height: 180px;
  }

  .modal-subtitle {
    display: none;
  }

  .shift-hint {
    display: none;
  }
}
</style>
